import { debug, initDebug, resolveListConfig, warn } from "./core/config.js";
import { attrSelector } from "./core/dom.js";
import { resolveSearchConfig } from "./global-search/config.js";
import { installHooksApi } from "./hooks/register.js";
import { SlaterList } from "./list.js";
import { initTypeahead } from "./typeahead/typeahead.js";

/**
 * Slater Search — a no-code, dependency-free replacement for Finsweet
 * Attributes' cmsfilter/cmsload on a Webflow CMS list, plus a no-code global
 * site search (`data-slater-search-list`) over every enabled collection and
 * static page.
 *
 *   <script src="https://api.slater.app/embed/v1/slater-search.js" defer></script>
 *
 * Unlike Finsweet, filtering and search run against a real SQL index rather than
 * the ~100 items Webflow rendered into the DOM, so results span the whole
 * collection and facet counts reflect the whole dataset.
 */

const lists: SlaterList[] = [];

function resultsContainerFor(wrapper: Element, key: string): Element | null {
  return (
    document.querySelector(attrSelector("data-slater-results", key)) ??
    wrapper.querySelector(".w-dyn-items") ??
    null
  );
}

function boot(): void {
  // Webflow re-runs custom code on some navigations; a second init would double-
  // bind every listener.
  if (window.__slaterSearchLoaded) return;
  window.__slaterSearchLoaded = true;

  initDebug();

  // Before the no-blocks early return: a project's site-wide JS registers
  // hooks on every page, so the API must exist (inert) even with no blocks.
  installHooksApi(lists);

  // Also before the early return: a typeahead input is usually a standalone
  // navbar box on pages with no list or search block at all.
  initTypeahead();

  const wrappers = Array.from(document.querySelectorAll("[data-slater-list]"));
  const searchWrappers = Array.from(document.querySelectorAll("[data-slater-search-list]"));
  if (wrappers.length === 0 && searchWrappers.length === 0) {
    debug("no [data-slater-list] or [data-slater-search-list] on this page — nothing to do");
    return;
  }

  for (const wrapper of wrappers) {
    const key = wrapper.getAttribute("data-slater-list");
    if (!key) continue;

    // Each list is isolated: a bad config, a missing template or a dead endpoint
    // must not stop the other lists on the page from working.
    try {
      const config = resolveListConfig(wrapper, key);
      if (!config) continue;

      const results = resultsContainerFor(wrapper, key);
      if (!results) {
        warn(`list "${key}": no [data-slater-results] or .w-dyn-items container found.`);
        continue;
      }

      const list = new SlaterList(config, wrapper, results);
      lists.push(list);
      debug(`list "${key}"`, config);
      void list.init();
    } catch (error) {
      warn(`list "${key}" failed to initialise:`, error);
    }
  }

  for (const wrapper of searchWrappers) {
    const key = wrapper.getAttribute("data-slater-search-list");
    if (!key) continue;

    try {
      // Lists and search blocks share one key namespace — controls target
      // either kind by key, so a duplicate would make ownership ambiguous.
      if (lists.some((list) => list.config.key === key)) {
        warn(`search "${key}": key already used on this page — pick a unique key.`);
        continue;
      }

      const config = resolveSearchConfig(wrapper, key);
      if (!config) continue;

      // A search container is a plain div the designer drew, so the wrapper
      // itself is the fallback — no .w-dyn-items to look for.
      const results =
        document.querySelector(attrSelector("data-slater-results", key)) ?? wrapper;

      const search = new SlaterList(config, wrapper, results);
      lists.push(search);
      debug(`search "${key}"`, config);
      void search.init();
    } catch (error) {
      warn(`search "${key}" failed to initialise:`, error);
    }
  }

  // Restoring from the back/forward cache does not re-run scripts, so the DOM we
  // built is still there but our listeners' state may not match it.
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) {
      for (const list of lists) list.rehydrate();
    }
  });
}

// Webflow populates .w-dyn-item during parse, so DOMContentLoaded is enough; a
// `defer`/late script may already be past it.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}